import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { SiweMessage } from 'siwe';
import { SignerProvider } from 'src/contexts/SignerContext';
import { SocketProvider } from 'src/contexts/SocketContext';
import { createContext } from 'src/helpers/context';
import {
  accessTokenIAO,
  desktopAddressKey,
  referralCodeIAO,
  removeAuthTokens,
} from 'src/helpers/storage';
import { LoginResponse, useLogin } from 'src/services/auth/login';
import { useVerifyToken } from 'src/services/auth/verify';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useChainId, useDisconnect, useSignMessage, WagmiProvider } from 'wagmi';

import { Modal, notification } from 'antd';
import { AccessCodeModal } from '../components/01.layout/header/AccessCodeModal';
import { GetReferralCodeModal } from '../components/01.layout/header/GetReferralCodeModal';
import { ERRORS } from '../constants/exchange';
import { PublicSocketProvider } from '../contexts/PublicSocketContext';
import { useDisclosure } from '../hooks/useDisclosure';
import { userKeys } from '../services/user/user-keys';
import { clearUserInfo, setUserInfo } from '../store/actions/user';
import { queryClient } from './react-query';

// imports
import { setupEthereumWallets } from '@near-wallet-selector/ethereum-wallets';
import { setupHereWallet } from '@near-wallet-selector/here-wallet';
import { setupHotWallet } from '@near-wallet-selector/hot-wallet';
import { setupMeteorWallet } from '@near-wallet-selector/meteor-wallet';
import { useWalletSelector, WalletSelectorProvider } from '@near-wallet-selector/react-hook';
import { connectModal, wagmiAdapter } from './web3-modal';

import { WalletModuleFactory } from '@near-wallet-selector/core';
import '@near-wallet-selector/modal-ui/styles.css';
import { useAppKitAccount } from '@reown/appkit/react';
import { useAppTranslation } from '../i18n/hooks';
import useGetUserAddress from '../hooks/useGetUserAddress';
import { near } from 'viem/chains';
import { ethereumMainnet } from './network-config';
import { returnRawValue, useLocalStorage } from '../hooks/useLocalStorage';

export const SWAP_NEAR_NETWORK = 'mainnet'; // Change to 'mainnet' for production

type AuthenticateStatus = 'authenticated' | 'loading' | 'verifying' | 'unauthenticated';

const [AuthStatusProvider, useAuthStatus] = createContext<{
  authStatus: AuthenticateStatus;
  setAuthStatus: (val: AuthenticateStatus) => void;
  openSignMessageModal: boolean;
  setOpenSignMessageModal: (val: boolean) => void;
}>({
  hookName: 'useAuthStatus',
  providerName: 'AuthStatusProvider',
});

export const useAuth = () => {
  const { authStatus, setAuthStatus, openSignMessageModal, setOpenSignMessageModal } =
    useAuthStatus();
  const { disconnectAsync } = useDisconnect();
  const { signIn: onSignIn, signedAccountId, signOut } = useWalletSelector();
  const { address: walletAddress } = useGetUserAddress();

  // useWalletButtonsInterceptor();
  const isEthereumWallet = useMemo(() => {
    if (!walletAddress) return false;
    return walletAddress.includes('0x');
  }, [walletAddress]);

  const signIn = async () => {
    try {
      if (isEthereumWallet) {
        await disconnectAsync();
      } else {
        await signOut();
      }
    } catch (e) {
      console.error(e);
    }

    onSignIn?.();
  };

  return {
    status: authStatus,
    isAuthenticated: authStatus === 'authenticated',
    isUnauthenticated: authStatus === 'unauthenticated',
    isPending: authStatus === 'loading',
    setAuthStatus: setAuthStatus,
    isLinkedDesktop: !!localStorage.getItem(desktopAddressKey),
    openSignMessageModal,
    setOpenSignMessageModal,
    isEthereumWallet,
    signIn,
  };
};

interface SignMessageModalProps {
  open: boolean;
  onClose: () => void;
  theme?: string;
  onOpenAccessModal?: () => void;
}

const SignMessageModal = ({ open, onClose, theme, onOpenAccessModal }: SignMessageModalProps) => {
  const { t } = useAppTranslation('notifications');
  const [loading, setLoading] = useState(true);
  const [accessToken] = useLocalStorage(accessTokenIAO, undefined, {
    serialize: returnRawValue,
    deserialize: returnRawValue,
  });
  const { signedAccountId, signOut, wallet } = useWalletSelector();
  const { address: evmAddress, isConnected: isEvmConnected } = useAppKitAccount();
  const { setAuthStatus, isAuthenticated, setOpenSignMessageModal } = useAuth();
  const { address: userAddress } = useGetUserAddress();

  const { disconnectAsync, disconnect } = useDisconnect();
  const chainId = useChainId();
  const { signMessageAsync } = useSignMessage();
  const dispatch = useAppDispatch();
  const loginMutation = useLogin();
  const verifyTokenQuery = useVerifyToken();

  const searchParams = new URLSearchParams(window.location.search);
  let referralCode = searchParams.get('referral');
  if (!referralCode) {
    referralCode = localStorage.getItem(referralCodeIAO);
  }
  const isEthereumWallet = useMemo(() => {
    if (!userAddress) return false;
    return userAddress.includes('0x');
  }, [userAddress, isEvmConnected]);

  const handleSignMessage = async () => {
    try {
      // Check if connected wallet is EVM or NEAR
      if (signedAccountId?.includes('0x')) {
        // Handle EVM wallet sign message (like MetaMask)
        const message = new SiweMessage({
          domain: window.location.host,
          address: evmAddress,
          statement: 'Sign in with Ethereum to the app.',
          uri: window.location.origin,
          version: '1',
          chainId: chainId,
        });
        const messageBody = message.prepareMessage();

        // Sign with EVM wallet using Reown AppKit
        try {
          const signature = await signMessageAsync({
            message: messageBody,
            account: evmAddress as any,
          });

          const data: LoginResponse = await loginMutation.mutateAsync({
            address: evmAddress!,
            signature,
            message: message.prepareMessage(),
            referrerCode: referralCode ?? evmAddress,
            chainId: chainId,
          });

          if (data.isNewUser) {
            Modal.info({
              icon: <span className="text-[22px] mr-[4px] translate-y-[-8px]">🥂</span>,
              title: 'Welcome to VDEX!',
              okText: 'Let’s GO!',
              content: (
                <>
                  <p>
                    Congratulations on becoming one of the few traders to get exclusive access to
                    VDEX in beta!
                  </p>
                  <br />
                  <p>
                    Since we are in beta, you might encounter some bugs or discrepancies. We
                    encourage you join{' '}
                    <a
                      href="https://t.me/+aJ2CtyNEb89mYjkx"
                      target="_blank"
                      className="text-[#1890ff]"
                      rel="noopener noreferrer"
                    >
                      Telegram
                    </a>{' '}
                    and report it for a chance to win exclusive badges and up to $5,000 in bounties.
                  </p>
                </>
              ),
            });
          }
          dispatch(
            setUserInfo({
              walletAddress: evmAddress,
              currentChain: chainId,
            }),
          );
          setAuthStatus('authenticated');
          setOpenSignMessageModal(false);
        } catch (error: any) {
          console.error('Error signing message:', error);
          if (error?.response?.data?.code === ERRORS.INVALID_OR_ALREADY_USED_REFERRAL_CODE.code) {
            onOpenAccessModal?.();
          }
        }
      } else {
        try {
          // Create NEAR sign message format
          const nearMessage = `Welcome to VDEX!\n\nClick to sign in and accept the VDEX Terms of Service.\n\nThis request will not trigger a blockchain transaction or cost any gas fees.\n\nWallet address:\n${signedAccountId}\n\nTimestamp: ${Date.now()}`;
          if (wallet?.signMessage) {
            // Create a 32-byte nonce as required by NEAR
            const nonce = Buffer.alloc(32);
            const signature = await wallet.signMessage({
              message: nearMessage,
              recipient: signedAccountId ?? '',
              nonce: nonce,
            });
            if (signature) {
              dispatch(
                setUserInfo({
                  walletAddress: signedAccountId!,
                  currentChain: near.id,
                }),
              );
              setAuthStatus('authenticated');
              setOpenSignMessageModal(false);
            }
          }
        } catch (signError: any) {
          console.error('Error signing NEAR message:', signError);
          notification.error({
            message: t('failedToLogin'),
            description: signError?.message || t('unexpectedError'),
            placement: 'bottomRight',
            style: { fontFamily: 'Poppins' },
          });
          return;
        }
      }
    } catch (error: any) {
      console.error('Error in handleSignMessage:', error);
      notification.error({
        message: t('failedToLogin'),
        description: error?.message || t('unexpectedError'),
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });
    }
  };

  const handleCancel = async () => {
    setOpenSignMessageModal(false);
    setAuthStatus('unauthenticated');
    removeAuthTokens();
    dispatch(clearUserInfo());
    try {
      if (isEthereumWallet) {
        await disconnectAsync();
      } else {
        await signOut();
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (signedAccountId && !isAuthenticated) {
      setOpenSignMessageModal(true);
    }

    if (
      signedAccountId &&
      userAddress &&
      isAuthenticated &&
      signedAccountId?.toLowerCase() !== userAddress?.toLowerCase()
    ) {
      setOpenSignMessageModal(true);
    }
  }, [signedAccountId, loading]);

  useEffect(() => {
    if (verifyTokenQuery.isError) {
      removeAuthTokens();
      dispatch(clearUserInfo());
      try {
        if (isEthereumWallet) {
          disconnect();
        } else {
          signOut();
        }
      } catch {}
      queryClient.resetQueries({
        queryKey: userKeys.all,
      });
    }
  }, [verifyTokenQuery.isError]);

  useEffect(() => {
    if (isEthereumWallet) {
      if (verifyTokenQuery.isSuccess) {
        setAuthStatus('authenticated');
      } else if (verifyTokenQuery.isFetching) {
        setAuthStatus('loading');
      } else if (verifyTokenQuery.isError) {
        setAuthStatus('unauthenticated');
      }
    } else if (!accessToken) {
      setAuthStatus('unauthenticated');
    }
  }, [verifyTokenQuery.status, isEthereumWallet]);

  useEffect(() => {
    if (signedAccountId && !signedAccountId.includes('0x')) {
      setAuthStatus('authenticated');
    }
  }, [signedAccountId]);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  return (
    <Modal
      destroyOnClose={true}
      open={open}
      onCancel={onClose}
      footer={null}
      width={360}
      centered
      className={`z-10 font-poppins deposit-modal overflow-hidden ${theme}`}
    >
      <div className="pt-[36px] pb-[24px] px-[18px] dark:bg-[#1a1b1f] rounded-[6px]">
        <div className="flex flex-col items-center">
          <img className="w-[40px] h-[40px]" src={'/images/sign-paper.png'} />
          <div className="my-[20px]">
            <p className="text-lg dark:text-white font-bold text-center">Verify your account</p>
            <p className="text-sm dark:text-[#fff9] text-center max-w-[280px]">
              To finish connecting, you must sign a message in your wallet to verify that you are
              the owner of this account.
            </p>
          </div>
          <div className="flex flex-col gap-[10px]">
            <button
              className="w-[130px] h-[28px] bg-[#3898ff] rounded-[12px] font-[700] text-white hover:scale-[1.025]"
              onClick={handleSignMessage}
              type="button"
              id="connect-button"
            >
              {'Sign message'}
            </button>
            <button onClick={handleCancel} className="dark:text-[#e0e8ff99] font-semibold">
              cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface WalletConnectProviderProps {
  children: React.ReactNode;
}

const WalletConnectProvider = ({ children }: WalletConnectProviderProps) => {
  const theme = useAppSelector((state) => state.theme);
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticateStatus>('loading');
  const [openSignMessageModal, setOpenSignMessageModal] = useState(false);
  const [openAccessCodeModal, { open: openAccessModal, close: closeAccessModal }] = useDisclosure();
  const [
    isOpenGetReferralCodeModal,
    { open: openGetReferralCodeModal, close: closeGetReferralCodeModal },
  ] = useDisclosure();
  const handleCloseSignMessageModal = () => {
    setOpenSignMessageModal(false);
  };

  return (
    <AuthStatusProvider
      value={{
        authStatus: authenticationStatus,
        setAuthStatus: setAuthenticationStatus,
        openSignMessageModal,
        setOpenSignMessageModal,
      }}
    >
      <div>
        {children}
        <SignMessageModal
          onClose={handleCloseSignMessageModal}
          open={openSignMessageModal}
          theme={theme}
          onOpenAccessModal={openAccessModal}
        />
        <AccessCodeModal
          open={openAccessCodeModal}
          onClose={closeAccessModal}
          onOk={openGetReferralCodeModal}
        />
        <GetReferralCodeModal
          open={isOpenGetReferralCodeModal}
          onClose={closeGetReferralCodeModal}
        />
      </div>
    </AuthStatusProvider>
  );
};

export const RainbowProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig} reconnectOnMount={true}>
      <QueryClientProvider client={queryClient}>
        <WalletSelectorProvider
          config={{
            network: SWAP_NEAR_NETWORK,
            modules: [
              setupEthereumWallets({
                wagmiConfig: wagmiAdapter.wagmiConfig as any,
                web3Modal: connectModal as any,
                chainId: ethereumMainnet.id,
              }),
              setupMeteorWallet(),
              setupHereWallet(),
              setupHotWallet(),
            ] as WalletModuleFactory[],
          }}
        >
          <WalletConnectProvider>
            <SignerProvider>
              <PublicSocketProvider>
                <SocketProvider>{children}</SocketProvider>
              </PublicSocketProvider>
            </SignerProvider>
          </WalletConnectProvider>
        </WalletSelectorProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};
